import React, { useEffect, useState } from 'react';
import { X, Edit, Mail, Phone, IdCard, MessageSquare, FileText, ChevronRight, Users, CheckCircle2, XCircle, Power, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { pessoaService, isPessoaAtiva } from '../../services/pessoaService';
import { financeiroService } from '../../services/financeiroService';
import { PessoaContato } from '../../types/pessoa';
import { Conta, TipoContaId } from '../../types/financeiro';
import { usePermissions } from '../../hooks/usePermissions';
import { MaskedValue, ProtectedField } from '../common/ProtectedField';

interface PessoaDetailsModalProps {
  pessoaId: number;
  onClose: () => void;
  onEdit: (pessoaId: number) => void;
}

export function PessoaDetailsModal({ pessoaId, onClose, onEdit }: PessoaDetailsModalProps) {
  const { canViewPeopleDocs, canViewPeopleBank, canViewFinancialValues, canEdit } = usePermissions();

  const [pessoa, setPessoa] = useState<any>(null);
  const [contatos, setContatos] = useState<PessoaContato[]>([]);
  const [enderecos, setEnderecos] = useState<any[]>([]);
  const [grupos, setGrupos] = useState<any[]>([]);
  const [dadosBancarios, setDadosBancarios] = useState<any[]>([]);
  const [contasPagar, setContasPagar] = useState<Conta[]>([]);
  const [contasReceber, setContasReceber] = useState<Conta[]>([]);
  const [loading, setLoading] = useState(true);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState<'contatos' | 'enderecos' | 'bancos' | 'financeiro'>('contatos');
  const [financeiroTab, setFinanceiroTab] = useState<'pagar' | 'receber'>('pagar');

  const handleToggleStatus = async () => {
    if (!pessoa) return;
    const atualAtivo = isPessoaAtiva(pessoa);
    const acao = atualAtivo ? 'DESATIVAR' : 'ATIVAR';
    if (!window.confirm(`Deseja realmente ${acao} "${pessoa.nome}" no banco de dados?`)) return;

    try {
      setIsTogglingStatus(true);
      await pessoaService.toggleAtivo(pessoa.id, !atualAtivo);
      await fetchData();
    } catch (err: any) {
      console.error('Error toggling status in details modal:', err);
      alert(`Erro ao ${acao.toLowerCase()}: ` + err.message);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pessoaId]);

  async function fetchData() {
    try {
      setLoading(true);
      const [pessoaData, contatosData, enderecosData, gruposData, bancariosData, pagarData, receberData] = await Promise.all([
        fetchPessoaDetails(pessoaId),
        pessoaService.getContatosByPessoa(pessoaId),
        pessoaService.getEnderecosByPessoa(pessoaId),
        pessoaService.getGruposByPessoa(pessoaId),
        pessoaService.getDadosBancariosByPessoa(pessoaId),
        financeiroService.getContasAbertasByPessoa(pessoaId, TipoContaId.PAGAR),
        financeiroService.getContasAbertasByPessoa(pessoaId, TipoContaId.RECEBER)
      ]);

      setPessoa(pessoaData);
      setContatos(contatosData);
      setEnderecos(enderecosData);
      setGrupos(gruposData);
      setDadosBancarios(bancariosData);
      setContasPagar(pagarData);
      setContasReceber(receberData);
    } catch (error) {
      console.error('Error fetching pessoa details:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchPessoaDetails(id: number) {
    const { data, error } = await supabase
      .from('pessoa')
      .select('*, tipo_cliente(nome), categoria_cliente(nome)')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-slate-600 font-medium">Carregando detalhes...</p>
        </div>
      </div>
    );
  }

  if (!pessoa) return null;

  const gruposString = grupos.map(g => g.grupo_pessoa?.nome).filter(Boolean).join(', ') || 'Nenhum grupo';
  const ativa = isPessoaAtiva(pessoa);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end z-50">
      <div className="bg-slate-50 w-full max-w-4xl h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-slate-900">Consulta de Pessoa</h2>
            {ativa ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                Ativo
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
                <XCircle className="w-3.5 h-3.5 text-rose-600" />
                Inativo
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleStatus}
              disabled={isTogglingStatus}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors cursor-pointer ${
                ativa
                  ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
                  : 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
              }`}
            >
              <Power className={`w-3.5 h-3.5 ${isTogglingStatus ? 'animate-spin' : ''}`} />
              {ativa ? 'Desativar no Banco' : 'Ativar no Banco'}
            </button>
            <ProtectedField permission="manage_people" hideMode="remove">
              <button
                onClick={() => onEdit(pessoa.id)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
            </ProtectedField>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Info Geral */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">ID</label>
              <p className="text-lg font-bold text-slate-900">#{pessoa.id}</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Criado em</label>
              <p className="text-slate-700">
                {pessoa.created_at ? (
                  new Date(pessoa.created_at).toLocaleString('pt-BR')
                ) : (
                  <span className="text-slate-400 text-sm italic">Não registrado (cadastro legado)</span>
                )}
              </p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Atualizado em</label>
              <p className="text-slate-700">
                {pessoa.updated_at ? (
                  new Date(pessoa.updated_at).toLocaleString('pt-BR')
                ) : (
                  <span className="text-slate-400 text-sm italic">Não registrado (cadastro legado)</span>
                )}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
            <div className={pessoa.nome_fantasia ? "md:col-span-2" : "md:col-span-3"}>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Razão Social / Nome</label>
              <p className="text-lg font-medium text-slate-900">{pessoa.nome}</p>
            </div>
            {pessoa.nome_fantasia && (
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Nome Fantasia</label>
                <p className="text-lg font-medium text-slate-900">{pessoa.nome_fantasia}</p>
              </div>
            )}
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Tipo de Cliente</label>
              <p className="text-slate-700">{pessoa.tipo_cliente?.nome || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Categoria</label>
              <p className="text-slate-700">{pessoa.categoria_cliente?.nome || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <IdCard className="w-3.5 h-3.5 text-purple-600" /> Documento (CPF/CNPJ)
              </label>
              <p className="text-slate-700 font-mono">
                <MaskedValue
                  value={pessoa.documento || '-'}
                  isAllowed={canViewPeopleDocs()}
                  mask="•••.•••.•••-••"
                />
              </p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-red-500" /> Email
              </label>
              <p className="text-slate-700">{pessoa.email || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-cyan-500" /> Fone
              </label>
              <p className="text-slate-700">{pessoa.fone || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Situação Cadastral</label>
              <p className="text-slate-700">{pessoa.situacao_cadastral || 'ATIVO'}</p>
            </div>
            {pessoa.cnae && (
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">CNAE</label>
                <p className="text-slate-700">{pessoa.cnae}</p>
              </div>
            )}
            {pessoa.data_inicio_atividade && (
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Início de Atividade</label>
                <p className="text-slate-700">{pessoa.data_inicio_atividade}</p>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-orange-500" /> Obs
              </label>
              <p className="text-slate-700">{pessoa.obs || '-'}</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-indigo-500" /> Grupos
              </label>
              <p className="text-slate-700">{gruposString}</p>
            </div>
          </div>

          {/* CNH Details (if present or Motorista) */}
          {(pessoa.numero_cnh || pessoa.categoria_cnh || pessoa.documento_cnh) && (
            <div className="bg-amber-50/60 p-6 rounded-xl border border-amber-200 shadow-sm space-y-4">
              <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2">
                <IdCard className="w-4 h-4 text-amber-600" />
                Dados da CNH / Motorista
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-xs font-bold text-amber-700 block uppercase">Nº CNH</span>
                  <p className="font-mono font-medium text-slate-900">{pessoa.numero_cnh || '-'}</p>
                </div>
                <div>
                  <span className="text-xs font-bold text-amber-700 block uppercase">Categoria</span>
                  <p className="font-bold text-slate-900">{pessoa.categoria_cnh || '-'}</p>
                </div>
                <div>
                  <span className="text-xs font-bold text-amber-700 block uppercase">Vencimento</span>
                  <p className="text-slate-900">
                    {pessoa.vencimento_cnh ? new Date(pessoa.vencimento_cnh).toLocaleDateString('pt-BR') : '-'}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-bold text-amber-700 block uppercase">Documento Anexo</span>
                  {pessoa.documento_cnh ? (
                    <a
                      href={pessoa.documento_cnh}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-red-600 font-bold hover:underline"
                    >
                      Ver Documento CNH
                    </a>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex border-b border-slate-200 bg-slate-50 overflow-x-auto">
              <button
                onClick={() => setActiveTab('contatos')}
                className={`px-6 py-3 text-sm font-bold transition-colors ${
                  activeTab === 'contatos' ? 'bg-white text-red-600 border-b-2 border-red-600' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Contatos ({contatos.length})
              </button>
              <button
                onClick={() => setActiveTab('enderecos')}
                className={`px-6 py-3 text-sm font-bold transition-colors ${
                  activeTab === 'enderecos' ? 'bg-white text-red-600 border-b-2 border-red-600' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Endereços ({enderecos.length})
              </button>
              <button
                onClick={() => setActiveTab('bancos')}
                className={`px-6 py-3 text-sm font-bold transition-colors ${
                  activeTab === 'bancos' ? 'bg-white text-red-600 border-b-2 border-red-600' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Dados Bancários ({dadosBancarios.length})
              </button>
              <button
                onClick={() => setActiveTab('financeiro')}
                className={`px-6 py-3 text-sm font-bold transition-colors ${
                  activeTab === 'financeiro' ? 'bg-white text-red-600 border-b-2 border-red-600' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Financeiro
              </button>
            </div>

            <div className="p-0">
              {activeTab === 'contatos' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Nome</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Telefone</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Obs</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {contatos.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-sm text-slate-500">Nenhum contato registrado.</td>
                        </tr>
                      ) : (
                        contatos.map(contato => (
                          <tr key={contato.id}>
                            <td className="px-6 py-4 text-sm text-slate-900">{contato.nome}</td>
                            <td className="px-6 py-4 text-sm text-slate-700">{contato.email}</td>
                            <td className="px-6 py-4 text-sm text-slate-700">{contato.telefone}</td>
                            <td className="px-6 py-4 text-sm text-slate-500">{contato.obs}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'enderecos' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Nome</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Cidade</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">CEP</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Rua</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Principal</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {enderecos.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-sm text-slate-500">Nenhum endereço registrado.</td>
                        </tr>
                      ) : (
                        enderecos.map(end => (
                          <tr key={end.id}>
                            <td className="px-6 py-4 text-sm text-slate-900">{end.nome}</td>
                            <td className="px-6 py-4 text-sm text-slate-700">{end.cidade?.nome || '-'}</td>
                            <td className="px-6 py-4 text-sm text-slate-700 font-mono">{end.cep}</td>
                            <td className="px-6 py-4 text-sm text-slate-700">
                              {end.rua}, {end.numero}
                              {end.complemento && ` - ${end.complemento}`}
                              <br />
                              <span className="text-xs text-slate-500">{end.bairro}</span>
                            </td>
                            <td className="px-6 py-4 text-sm">
                              {end.principal === 'S' || end.principal === '1' || end.principal === true ? (
                                <span className="text-emerald-600 font-bold">Sim</span>
                              ) : (
                                <span className="text-slate-400">Não</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'bancos' && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Banco</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Agência / Conta</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Forma Pgto</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Chave PIX</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Titular / CPF</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {dadosBancarios.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-sm text-slate-500">Nenhum dado bancário registrado.</td>
                        </tr>
                      ) : (
                        dadosBancarios.map(d => (
                          <tr key={d.id}>
                            <td className="px-6 py-4 text-sm font-medium text-slate-900">{d.banco?.nome || '-'}</td>
                            <td className="px-6 py-4 text-sm text-slate-700 font-mono">
                              <MaskedValue
                                value={`Ag: ${d.agencia || '-'} / CC: ${d.conta || '-'}`}
                                isAllowed={canViewPeopleBank()}
                                mask="Ag: •••• / CC: •••••-•"
                              />
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-700">{d.forma_pagamento?.nome || '-'}</td>
                            <td className="px-6 py-4 text-sm text-slate-700 font-mono">
                              <MaskedValue
                                value={d.pix || '-'}
                                isAllowed={canViewPeopleBank()}
                                mask="••••••••••••"
                              />
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-700">
                              {d.nome || '-'} {d.cpf && (
                                <MaskedValue
                                  value={`(${d.cpf})`}
                                  isAllowed={canViewPeopleDocs()}
                                  mask="(•••.•••.•••-••)"
                                />
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'financeiro' && (
                <div className="flex flex-col h-full">
                  <div className="flex bg-slate-100 p-1 m-4 rounded-lg border border-slate-200">
                    <button
                      onClick={() => setFinanceiroTab('pagar')}
                      className={`flex-1 py-2 text-xs font-black uppercase tracking-tighter rounded-md transition-all ${
                        financeiroTab === 'pagar' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Contas a Pagar em Aberto
                    </button>
                    <button
                      onClick={() => setFinanceiroTab('receber')}
                      className={`flex-1 py-2 text-xs font-black uppercase tracking-tighter rounded-md transition-all ${
                        financeiroTab === 'receber' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      Contas a Receber em Aberto
                    </button>
                  </div>

                  <div className="px-4 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-bold text-slate-700">
                        {financeiroTab === 'pagar' ? 'Débitos Pendentes' : 'Créditos Pendentes'}
                      </h4>
                      <button className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 rounded-lg transition-colors">
                        <FileText className="w-3.5 h-3.5" />
                        Gerar Extrato
                      </button>
                    </div>

                    <div className="overflow-x-auto border border-slate-200 rounded-xl">
                      <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-[10px] font-black uppercase text-slate-500">Vencimento</th>
                            <th className="px-4 py-2 text-left text-[10px] font-black uppercase text-slate-500">Descrição</th>
                            <th className="px-4 py-2 text-left text-[10px] font-black uppercase text-slate-500">Valor</th>
                            <th className="px-4 py-2 text-left text-[10px] font-black uppercase text-slate-500">Categoria</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                          {(financeiroTab === 'pagar' ? contasPagar : contasReceber).length === 0 ? (
                            <tr>
                              <td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-500">Nenhuma conta em aberto.</td>
                            </tr>
                          ) : (
                            (financeiroTab === 'pagar' ? contasPagar : contasReceber).map(conta => (
                              <tr key={conta.id} className="hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm text-slate-700">
                                  {conta.dt_vencimento ? new Date(conta.dt_vencimento).toLocaleDateString('pt-BR') : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-900 font-medium">{conta.descricao}</td>
                                <td className={`px-4 py-3 text-sm font-bold ${financeiroTab === 'pagar' ? 'text-red-600' : 'text-emerald-600'}`}>
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(conta.valor)}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-500">
                                  {(conta as any).categoria?.nome || '-'}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-slate-200 px-6 py-4 flex justify-end sticky bottom-0 z-10">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
